import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import Header from '../Header';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';
import { getErrorMessageKey } from '../../services/online/errorMessages';

const PERIODS = ['WEEKLY', 'MONTHLY', 'YEARLY', 'GLOBAL'];

const RANKING_TYPES = [
    'MATCHES_PLAYED',
    'WIN_RATE',
    'CAIDAS',
    'CONTRACAIDAS',
    'SAN_VICENTES',
    'RONDAS_RONDINES',
    'ASEOS',
    'BACIGAS_CANTADAS',
    'LAS_COSAS',
    'LAS_31',
    'MATCH_BLOWOUT',
    'ROUND_BLOWOUT_RONDA',
    'ROUND_BLOWOUT_BACIGA',
];

const BLOWOUT_TYPES = new Set(['MATCH_BLOWOUT', 'ROUND_BLOWOUT_RONDA', 'ROUND_BLOWOUT_BACIGA']);

/** One player's leaderboard row — highlighted (gold) when it's the viewer's own. */
function LeaderboardRow({ entry, isMine, t }) {
    return (
        <Card elevation={isMine ? 4 : 1} sx={isMine ? { border: '2px solid #ffd700' } : undefined}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="h6" sx={{ minWidth: 32, color: 'text.secondary' }}>
                        #{entry.rank}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: isMine ? 'bold' : 'normal' }}>
                        {entry.userName || '?'}
                    </Typography>
                </Stack>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {entry.matchesPlayed !== undefined ? `${entry.value}% ` : entry.value}
                    {entry.matchesPlayed !== undefined && (
                        <Typography component="span" variant="body2" color="text.secondary">
                            ({t('online.rankings.matches_played', { count: entry.matchesPlayed })})
                        </Typography>
                    )}
                </Typography>
            </CardContent>
        </Card>
    );
}

/** One "biggest blowout" row — a specific finished match/round, not a player. */
function BlowoutRow({ entry }) {
    return (
        <Card elevation={1}>
            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        #{entry.rank} · {new Date(entry.finishedAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="h6">
                        {entry.scoreTeam1} - {entry.scoreTeam2}
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {entry.players
                        .filter((p) => p.team === entry.winningTeam)
                        .map((p) => p.userName || '?')
                        .join(' & ')}
                </Typography>
            </CardContent>
        </Card>
    );
}

/**
 * Reads GET /rankings — one of 13 nightly-precomputed leaderboards (see the
 * rankings feature plan), each viewable across 4 periods. Mirrors
 * OnlineHistoryScreen.jsx's fetch/loading/empty pattern; period is a Tabs
 * row (only 4 options), category is a Select (13 options — too many for a
 * single Tabs row on mobile).
 */
export default function OnlineRankingsScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { authFetch, user } = useAuth();

    const [period, setPeriod] = useState('GLOBAL');
    const [type, setType] = useState('MATCHES_PLAYED');
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const reportError = useCallback(
        (err) => setError(t(getErrorMessageKey(err instanceof ApiError ? err.code : undefined))),
        [t]
    );

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        (async () => {
            try {
                const result = await authFetch(`/rankings?type=${type}&period=${period}&limit=20`);
                if (!cancelled) setData(result);
            } catch (err) {
                if (!cancelled) reportError(err);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [authFetch, period, type, reportError]);

    const isBlowout = BLOWOUT_TYPES.has(type);

    return (
        <>
            <Header title={t('online.rankings.title')} onBack={() => navigate('/online')} showSettings={false} />
            <Container maxWidth="sm" sx={{ mt: 2, mb: 4 }}>
                <Stack spacing={2}>
                    <Tabs
                        value={period}
                        onChange={(_e, value) => setPeriod(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '1rem',
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                            px: 1,
                        }}
                    >
                        {PERIODS.map((p) => (
                            <Tab key={p} value={p} label={t(`online.rankings.period_${p.toLowerCase()}`)} />
                        ))}
                    </Tabs>

                    <FormControl fullWidth size="small">
                        <InputLabel id="ranking-category-label">{t('online.rankings.category_label')}</InputLabel>
                        <Select
                            labelId="ranking-category-label"
                            value={type}
                            label={t('online.rankings.category_label')}
                            onChange={(e) => setType(e.target.value)}
                        >
                            {RANKING_TYPES.map((rt) => (
                                <MenuItem key={rt} value={rt}>
                                    {t(`online.rankings.category_${rt}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {error && <Alert severity="error">{error}</Alert>}

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : !data || data.entries.length === 0 ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            {t('online.rankings.empty')}
                        </Typography>
                    ) : (
                        <Stack spacing={1}>
                            {data.entries.map((entry) =>
                                isBlowout ? (
                                    <BlowoutRow key={entry.id} entry={entry} />
                                ) : (
                                    <LeaderboardRow key={entry.userId} entry={entry} isMine={entry.userId === user?.id} t={t} />
                                )
                            )}
                            {data.currentUser && (
                                <>
                                    <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
                                        {t('online.rankings.your_rank_label')}
                                    </Typography>
                                    <LeaderboardRow entry={data.currentUser} isMine t={t} />
                                </>
                            )}
                            {data.computedAt && (
                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 1 }}>
                                    {t('online.rankings.computed_at', { date: new Date(data.computedAt).toLocaleString() })}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Stack>
            </Container>
        </>
    );
}
